import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import api from '../utils/api';

const formatAsOfDate = (dateStr: string | null | undefined): string | null => {
  if (!dateStr || dateStr === 'None') return null;
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}-${dd}-${yyyy}`;
  } catch {
    return null;
  }
};

const CitationReader: React.FC = () => {
  const { citation_uuid } = useParams<{ citation_uuid: string }>();
  const [searchParams] = useSearchParams();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [citationTitle, setCitationTitle] = useState<string>('');
  const [citationAsOfDate, setCitationAsOfDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCitation = async () => {
      if (!citation_uuid) {
        setError('Citation UUID is required');
        setLoading(false);
        return;
      }

      try {
        // Fetch metadata for title and as of date (non-blocking)
        try {
          console.warn('[CitationReader] Fetching metadata for', citation_uuid);
          const metaResponse = await api.get(`/api/citation/${citation_uuid}/metadata`);
          console.warn('[CitationReader] Metadata response:', metaResponse.data);
          setCitationTitle(metaResponse.data.title || 'Citation');
          setCitationAsOfDate(formatAsOfDate(metaResponse.data.as_of_date));
        } catch (metaErr) {
          console.warn('[CitationReader] Metadata fetch failed:', metaErr);
          setCitationTitle('Citation');
        }

        const response = await api.get(`/api/citation/${citation_uuid}/read`, {
          responseType: 'blob'
        });

        const blob = response.data;
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
      } catch (err) {
        console.error('Failed to fetch citation:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch citation');
      } finally {
        setLoading(false);
      }
    };

    fetchCitation();

    // Cleanup function to revoke object URL
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [citation_uuid]);

  // Extract page number from query parameters
  const page = searchParams.get('page');
  const pdfUrlWithPage = page && pdfUrl ? `${pdfUrl}#page=${page}` : pdfUrl;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-adalabs-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading citation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Citation</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!pdfUrl) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">No PDF content available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-white">
      <div className="absolute top-0 left-0 right-0 z-10 bg-gray-50 px-4 py-2 border-b flex justify-between items-center">
        <div>
          <h1 className="text-lg font-semibold text-gray-800">
            {citationTitle || 'Citation Reader'}
            {citationAsOfDate && (
              <span className="text-gray-500 font-normal ml-2">
                (As of {citationAsOfDate})
              </span>
            )}
          </h1>
          {page && (
            <p className="text-sm text-blue-600">Page: {page}</p>
          )}
        </div>
        <button 
          onClick={() => window.history.back()}
          className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded text-gray-700 transition-colors"
        >
          ← Back
        </button>
      </div>
      <div className="pt-16 h-full">
        <iframe
          src={pdfUrlWithPage || ''}
          className="w-full h-full border-0"
          title="Citation PDF"
        />
      </div>
    </div>
  );
};

export default CitationReader; 